import axiosInstance from '../api';
import { mlsListings, buyerPropertyMatches } from '@prisma/client';

interface SearchParams {
  naturalLanguage?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zip?: string;
  minPrice?: number;
  maxPrice?: number;
  minBeds?: number;
  maxBeds?: number;
  minBaths?: number;
  maxBaths?: number;
  propertyType?: string;
  minSqft?: number;
  maxSqft?: number;
  features?: string[];
  applyPreferences?: boolean;
}

interface PropertyMatch extends buyerPropertyMatches {
  mlsListing: mlsListings;
}

interface SearchResult {
  properties: any[];
  matchedCount: number;
  searchParams: any;
  appliedPreferences?: boolean;
}

const aiPropertySearchService = {
  // Search for properties with AI
  search: async (params: SearchParams): Promise<SearchResult> => {
    const res = await axiosInstance.post<SearchResult>(
      '/ai-property-search',
      params
    );
    return res.data;
  },

  // Get user's matched properties
  getMatches: async (limit?: number): Promise<PropertyMatch[]> => {
    const res = await axiosInstance.get('/ai-property-search', {
      params: { limit },
    });
    // Backend returns { matches, matchedCount, newMatchCount, previouslyNotifiedCount }
    // Extract just the matches array
    return res.data.matches || [];
  },

  // Get a specific match
  getMatch: async (id: string): Promise<PropertyMatch> => {
    const res = await axiosInstance.get<PropertyMatch>(
      `/ai-property-search/${id}`
    );
    return res.data;
  },

  // Update match status (viewed, saved, dismissed)
  updateMatch: async (
    id: string,
    updates: {
      isViewed?: boolean;
      isSaved?: boolean;
      isDismissed?: boolean;
    }
  ): Promise<PropertyMatch> => {
    const res = await axiosInstance.patch<PropertyMatch>(
      `/ai-property-search/${id}`,
      updates
    );
    return res.data;
  },

  // Mark as viewed
  markViewed: async (id: string): Promise<PropertyMatch> => {
    return aiPropertySearchService.updateMatch(id, { isViewed: true });
  },

  // Save a match
  saveMatch: async (id: string): Promise<PropertyMatch> => {
    return aiPropertySearchService.updateMatch(id, { isSaved: true });
  },

  // Dismiss a match
  dismissMatch: async (id: string): Promise<PropertyMatch> => {
    return aiPropertySearchService.updateMatch(id, { isDismissed: true });
  },

  // Search with natural language
  searchNaturalLanguage: async (
    query: string,
    applyPreferences = true
  ): Promise<SearchResult> => {
    return aiPropertySearchService.search({
      naturalLanguage: query,
      applyPreferences,
    });
  },

  // Get saved matches only
  getSavedMatches: async (): Promise<PropertyMatch[]> => {
    const matches = await aiPropertySearchService.getMatches();
    return matches.filter((m) => m.isSaved);
  },
};

export default aiPropertySearchService;
export type { SearchParams, PropertyMatch, SearchResult };
